// src/utils/seo.js — Comprehensive SEO utilities for dynamic meta tags and structured data

// Update page meta tags dynamically
export function updateMetaTags({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
}) {
  // Update title
  if (title) {
    document.title = `${title} | DG HelpMate`;
    updateTag('meta', 'name', 'twitter:title', title + ' | DG HelpMate');
  }

  // Update description
  if (description) {
    updateTag('meta', 'name', 'description', description);
    updateTag('meta', 'property', 'og:description', description);
    updateTag('meta', 'name', 'twitter:description', description);
  }

  // Update keywords
  if (keywords) {
    updateTag('meta', 'name', 'keywords', keywords);
  }

  // Update canonical URL
  if (canonical) {
    updateTag('link', 'rel', 'canonical', canonical, 'href');
  }

  // Update OG image
  if (ogImage) {
    updateTag('meta', 'property', 'og:image', ogImage);
    updateTag('meta', 'name', 'twitter:image', ogImage);
  }

  // Update OG type
  updateTag('meta', 'property', 'og:type', ogType);

  // Update Twitter card type
  updateTag('meta', 'name', 'twitter:card', twitterCard);
}

// Helper to update or create meta/link tags
function updateTag(tagName, attrName, attrValue, content, contentAttr = 'content') {
  let tag = document.querySelector(`${tagName}[${attrName}="${attrValue}"]`);
  
  if (!tag) {
    tag = document.createElement(tagName);
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  
  tag.setAttribute(contentAttr, content);
}

// Generate structured data for different page types
export const structuredData = {
  // Organization schema (already in index.html, but can be updated dynamically)
  organization: (orgData = {}) => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: orgData.name || 'DG HelpMate',
    alternateName: ['DGHelpMate', 'Digi HelpMate', 'Digital HelpMate'],
    url: orgData.url || 'https://www.dghelpmate.com',
    logo: orgData.logo || 'https://www.dghelpmate.com/images/logo.png',
    description: orgData.description || 'Professional digital content support for coaching institutes.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Arwal',
      addressRegion: 'Bihar',
      postalCode: '804402',
      addressCountry: 'IN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+91-9241653369',
      contactType: 'customer service',
    },
    sameAs: [
      'https://www.instagram.com/dghelpmate',
      'https://www.youtube.com/@dghelpmate',
    ],
  }),

  // LocalBusiness schema
  localBusiness: (businessData = {}) => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: businessData.name || 'DG HelpMate',
    image: businessData.image || 'https://www.dghelpmate.com/images/logo.png',
    url: businessData.url || 'https://www.dghelpmate.com',
    telephone: '+91-9241653369',
    email: 'support@dghelpmate.in',
    priceRange: '₹₹',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rampur Chauram',
      addressLocality: 'Arwal',
      addressRegion: 'Bihar',
      postalCode: '804402',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 25.2,
      longitude: 84.6,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '21:00',
    },
  }),

  // Service schema for services page
  service: (serviceData) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceData.serviceType,
    provider: {
      '@type': 'Organization',
      name: 'DG HelpMate',
      url: 'https://www.dghelpmate.com',
    },
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    description: serviceData.description,
    offers: {
      '@type': 'Offer',
      price: serviceData.price,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
    },
  }),

  // Product schema for bundles/courses
  product: (productData) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productData.name,
    description: productData.description,
    image: productData.image,
    brand: {
      '@type': 'Brand',
      name: 'DG HelpMate',
    },
    offers: {
      '@type': 'Offer',
      price: productData.price,
      priceCurrency: 'INR',
      availability: productData.availability || 'https://schema.org/InStock',
      url: productData.url,
    },
    aggregateRating: productData.rating ? {
      '@type': 'AggregateRating',
      ratingValue: productData.rating.value,
      reviewCount: productData.rating.count,
      bestRating: '5',
      worstRating: '1',
    } : undefined,
  }),

  // Course schema
  course: (courseData) => ({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: courseData.name,
    description: courseData.description,
    provider: {
      '@type': 'Organization',
      name: 'DG HelpMate',
      url: 'https://www.dghelpmate.com',
      sameAs: 'https://www.instagram.com/dghelpmate',
    },
    offers: {
      '@type': 'Offer',
      price: courseData.price,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      url: courseData.url,
    },
    educationalLevel: courseData.educationalLevel || 'Intermediate',
    courseLanguage: 'hi',
    inLanguage: 'hi',
  }),

  // BlogPosting schema for blog posts
  blogPosting: (blogData) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blogData.headline,
    description: blogData.description,
    image: blogData.image,
    datePublished: blogData.datePublished,
    dateModified: blogData.dateModified,
    author: {
      '@type': 'Person',
      name: blogData.author || 'DG HelpMate',
      url: 'https://www.dghelpmate.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'DG HelpMate',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.dghelpmate.com/images/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': blogData.url,
    },
  }),

  // FAQPage schema
  faqPage: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }),

  // BreadcrumbList schema
  breadcrumbList: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }),

  // Review schema
  review: (reviewData) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': reviewData.itemType || 'Service',
      name: reviewData.itemName,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: reviewData.rating,
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: reviewData.author,
    },
    reviewBody: reviewData.body,
    datePublished: reviewData.date,
  }),
};

// Inject structured data into page
export function injectStructuredData(data) {
  // Remove existing structured data of the same type if exists
  const existing = document.querySelector(`script[type="application/ld+json"][data-schema-type="${data['@type']}"]`);
  if (existing) {
    existing.remove();
  }

  // Create new script tag
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema-type', data['@type']);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

// Remove structured data by type
export function removeStructuredData(type) {
  const script = document.querySelector(`script[type="application/ld+json"][data-schema-type="${type}"]`);
  if (script) {
    script.remove();
  }
}

// Preconnect to important origins
export function addPreconnects() {
  const origins = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://firestore.googleapis.com',
    'https://storage.googleapis.com',
  ];

  origins.forEach(origin => {
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
}

// Add DNS prefetch for external resources
export function addDnsPrefetch() {
  const origins = [
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com',
    'https://wa.me',
  ];

  origins.forEach(origin => {
    if (!document.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`)) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = origin;
      document.head.appendChild(link);
    }
  });
}

// Generate dynamic sitemap entries (for client-side routing)
export function generateSitemapEntries(pages) {
  const baseUrl = 'https://www.dghelpmate.com';
  const lastmod = new Date().toISOString().split('T')[0];
  
  return pages.map(page => ({
    loc: `${baseUrl}${page.path}`,
    lastmod: page.lastmod || lastmod,
    changefreq: page.changefreq || 'monthly',
    priority: page.priority || '0.5',
  }));
}

// SEO page configurations
export const seoConfig = {
  home: {
    title: 'Digital Support for Coaching Institutes',
    description: 'DG HelpMate — Professional MCQ Question Banks, PPT Designing, YouTube Thumbnail, SEO & Channel Management for coaching institutes. Trusted by 26+ institutes.',
    keywords: 'DG HelpMate, coaching content, MCQ bank, PPT designing, YouTube SEO, Bihar education',
  },
  services: {
    title: 'Our Services',
    description: 'MCQ Question Banks, PPT Notes, Test Papers, Thumbnails, Typing Services — everything your coaching institute needs.',
    keywords: 'MCQ services, PPT designing, test papers, thumbnail design, typing services Bihar',
  },
  bundles: {
    title: 'Ready-made Content Bundles',
    description: 'Pre-built content bundles with custom branding for coaching institutes. Save time and get professional quality materials.',
    keywords: 'content bundles, ready-made MCQs, coaching materials, custom branding',
  },
  courses: {
    title: 'Online Courses',
    description: 'Learn digital teaching skills — PPT designing, AI tools, YouTube growth, fast typing, thumbnail creation with DG HelpMate courses.',
    keywords: 'online courses, teacher training, digital teaching, PPT course, YouTube course',
  },
  blog: {
    title: 'Blog & Resources',
    description: 'Teaching tips, digital marketing strategies, and educational insights for coaching institute teachers.',
    keywords: 'teaching blog, education tips, digital marketing for teachers, coaching insights',
  },
  about: {
    title: 'About Us',
    description: 'Learn about DG HelpMate — Bihar\'s trusted education content partner. MSME Registered, serving 500+ coaching institutes.',
    keywords: 'about DG HelpMate, Govardhan Pandit, education company Bihar, MSME registered',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with DG HelpMate. WhatsApp, email, or visit us in Arwal, Bihar for professional content services.',
    keywords: 'contact DG HelpMate, WhatsApp support, coaching content contact',
  },
};

export default {
  updateMetaTags,
  structuredData,
  injectStructuredData,
  removeStructuredData,
  addPreconnects,
  addDnsPrefetch,
  generateSitemapEntries,
  seoConfig,
};